// === Batch availability cache (single owner) ===
//
// Batch dropdowns show LIVE stock. The cache exists only to stop one dialog
// re-asking the API for the same product on every render — it must never be
// the reason a user cannot see stock they just added.
//
// Two rules this module enforces, both of which were broken before:
//  1. Entries are scoped to the ACTING COMPANY, not the logged-in user. A
//     superadmin/agronomist switches company with a client-side navigate (no
//     page reload), so a user-keyed cache carried one company's batches — or a
//     cached empty list — straight into the next company.
//  2. Entries expire, and any stock-changing request drops them immediately
//     (see invalidateBatchCacheForPath, wired into apiFetch). Previously the
//     only invalidation was saving a spray task or a full page reload, so
//     "added 40 L, batch still not in the dropdown" persisted for the whole
//     session.
//
// This module imports nothing from the app so it can be used by both the
// api-client and the hooks without a cycle.

export interface CachedBatchOption {
  readonly batch_number: string;
  readonly available_qty: number;
  readonly pending_qty?: number;
  readonly pending_task_ids?: readonly number[];
}

/** How long a batch list may be reused before it is re-fetched. */
export const BATCH_CACHE_TTL_MS = 30_000;

interface CacheEntry {
  readonly batches: readonly CachedBatchOption[];
  readonly storedAt: number;
}

// Key: `${companyId}|${productType}_${productId}`
const cache = new Map<string, CacheEntry>();

let version = 0;
const listeners = new Set<() => void>();

/** Cache key for one product under one acting company. */
export function batchCacheKey(
  companyId: string,
  productType: string,
  productId: number,
): string {
  return `${companyId}|${productType}_${productId}`;
}

/**
 * Bumped every time the cache is cleared. Hooks compare it so a dialog opened
 * after an invalidation re-fetches instead of reusing state it already holds.
 */
export function getBatchCacheVersion(): number {
  return version;
}

/** Read a cached batch list, or null when absent or past its TTL. */
export function getCachedBatches(key: string): readonly CachedBatchOption[] | null {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.storedAt > BATCH_CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }
  return entry.batches;
}

export function setCachedBatches(key: string, batches: readonly CachedBatchOption[]): void {
  cache.set(key, { batches, storedAt: Date.now() });
}

/**
 * Drop every cached batch list and notify open dialogs.
 * Called after any stock movement is written (order received, stocktake,
 * spray/observation saved) so the next read comes from the API.
 */
export function clearBatchCache(): void {
  cache.clear();
  version += 1;
  for (const listener of listeners) listener();
}

/** Subscribe to invalidations. Returns an unsubscribe function. */
export function subscribeBatchCache(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// API paths whose non-GET requests can change on-hand stock. Prefix-matched,
// so new endpoints under these roots are covered without another edit here.
const STOCK_MUTATING_PREFIXES = [
  '/api/v1/stock-inventory',
  '/api/v1/chemical-stock-movements',
  '/api/v1/fertilizer-stock-movements',
  '/api/v1/spray-tasks',
  '/api/v1/irrigation-tasks',
  '/api/v1/pest-disease-observations',
];

// POST endpoints under those roots that only READ. The batch lookup itself is a
// POST to /spray-tasks/batch-numbers — treating it as a write would clear the
// cache on every fetch, wake every subscriber, and fetch again forever.
// (Photo uploads sit under one of those roots too and move no stock.)
const READ_ONLY_POST_PATHS = [
  '/api/v1/spray-tasks/batch-numbers',
  '/api/v1/pest-disease-observations/upload-image',
];

/**
 * Invalidate the cache when a request has just changed stock.
 * Wired into apiFetch so no future stock path can forget to call it.
 */
export function invalidateBatchCacheForPath(path: string, method: string | undefined): void {
  const verb = (method ?? 'GET').toUpperCase();
  if (verb === 'GET' || verb === 'HEAD') return;
  const pathname = path.split('?')[0] ?? path;
  if (READ_ONLY_POST_PATHS.includes(pathname)) return;
  if (!STOCK_MUTATING_PREFIXES.some((p) => pathname.startsWith(p))) return;
  clearBatchCache();
}
